import {
  Target,
  HowToSaveType,
  howToSaveCash,
  howToSaveInvestmentProducts,
  TargetTypes,
  TargetType,
  TargetAmount,
} from './type';

// MEMO：'reserveAmountRegistered' -> 毎月の積立額設定済 typeは投資商品or現金　'reserveAmountNotRegistered' -> 毎月の積立額未設定
export type TargetAnnualYieldState =
  | {
      tag: 'reserveAmountRegistered';
      type: HowToSaveType;
    }
  | { tag: 'reserveAmountNotRegistered' };

export const targetToHowToSave = (
  target: Target | null
): TargetAnnualYieldState => {
  if (target === null) {
    return { tag: 'reserveAmountNotRegistered' } as const;
  } else {
    if (
      target.accountList !== null &&
      target.excludeAmount !== null &&
      target.reserveAmount !== null
    ) {
      if (target.yield === 0 || target.yield === null) {
        return {
          tag: 'reserveAmountRegistered',
          type: howToSaveCash,
        } as { tag: 'reserveAmountRegistered'; type: HowToSaveType };
      } else {
        return {
          tag: 'reserveAmountRegistered',
          type: howToSaveInvestmentProducts,
        } as { tag: 'reserveAmountRegistered'; type: HowToSaveType };
      }
    } else {
      return { tag: 'reserveAmountNotRegistered' } as const;
    }
  }
};

export const targetTypes: TargetTypes[] = [
  {
    targetType: 1,
    accounts: ['確定拠出年金', 'つみたてNISA'],
    isRecommendMark: true,
    title: '老後資金',
    roughTargetAmount: 20000000,
    description: '夫婦/65歳までに',
    subTitle: '老後資金',
    sort: 1,
  },
  {
    targetType: 2,
    accounts: ['預金口座(貯蓄用)', '証券口座'],
    isRecommendMark: false,
    title: '早期リタイア（FIRE）',
    roughTargetAmount: 100000000,
    description: '単身者の場合',
    subTitle: 'FIRE',
    sort: 2,
  },
  {
    targetType: 3,
    accounts: ['ジュニアNISA', '子供名義の預金口座'],
    isRecommendMark: false,
    title: '子どもの教育資金',
    roughTargetAmount: 4000000,
    description: '大学入学までに',
    subTitle: '教育資金',
    sort: 3,
  },
  {
    targetType: 4,
    title: '海外留学',
    roughTargetAmount: 2500000,
    description: '米国6ヶ月の留学',
    subTitle: '海外留学',
    sort: 14,
  },
  {
    targetType: 5,
    accounts: ['預金口座(貯蓄用)', '証券口座'],
    isRecommendMark: false,
    title: '住宅ローン頭金',
    roughTargetAmount: 6000000,
    description: '購入価格の約20％',
    subTitle: '住宅頭金',
    sort: 4,
  },
  {
    targetType: 6,
    title: 'リフォーム費用',
    roughTargetAmount: 2500000,
    description: '部分的な改造•修繕',
    subTitle: 'リフォム',
    sort: 8,
  },
  {
    targetType: 7,
    title: '引越し資金',
    roughTargetAmount: 1000000,
    description: '引越し代•敷金礼金等',
    subTitle: '引越資金',
    sort: 9,
  },
  {
    targetType: 8,
    title: '自分の店を持つ',
    roughTargetAmount: 9000000,
    description: '小規模店舗の開店資金',
    subTitle: '自分の店',
    sort: 13,
  },
  {
    targetType: 9,
    title: '起業する',
    roughTargetAmount: 3000000,
    description: 'オフィス賃貸•諸経費',
    subTitle: '起業する',
    sort: 12,
  },
  {
    targetType: 10,
    title: '結婚資金',
    roughTargetAmount: 3000000,
    description: '挙式・披露宴',
    subTitle: '結婚資金',
    sort: 5,
  },
  {
    targetType: 11,
    title: 'リゾート挙式',
    roughTargetAmount: 3000000,
    description: '少人数の挙式•旅費等',
    subTitle: '海外挙式',
    sort: 15,
  },
  {
    targetType: 12,
    title: '新婚旅行',
    roughTargetAmount: 800000,
    description: '海外旅行2人分',
    subTitle: '新婚旅行',
    sort: 11,
  },
  {
    targetType: 13,
    title: '旅行資金',
    roughTargetAmount: 300000,
    description: 'ヨーロッパ5泊6日/1人',
    subTitle: '旅行資金',
    sort: 6,
  },
  {
    targetType: 14,
    title: '世界一周',
    roughTargetAmount: 3000000,
    description: '90日間のクルーズ',
    subTitle: '世界一周',
    sort: 16,
  },
  {
    targetType: 15,
    title: '車の購入費用',
    roughTargetAmount: 2000000,
    description: 'コンパクトカーの場合',
    subTitle: '新車資金',
    sort: 7,
  },
  {
    targetType: 16,
    title: '不動産投資の初期費用',
    roughTargetAmount: 1000000,
    description: '1500万円程度の物件',
    subTitle: '投資資金',
    sort: 10,
  },
  {
    targetType: 17,
    title: 'その他の目標',
    roughTargetAmount: null,
    description: '',
    subTitle: 'その他',
    sort: 17,
  },
];

export const isTargetType = (targetType: unknown): targetType is TargetType => {
  return (
    typeof targetType === 'number' &&
    targetType === Math.ceil(targetType) &&
    targetType >= 1 &&
    targetType <= 17
  );
};

export const targetTypeToImg = (targetType: TargetType): string =>
  `/static/images/advisor/goal${targetType}.svg`;

export const targetTypeToIconImg = (targetType: TargetType): string =>
  `/static/images/advisor/icon-goal${targetType}.svg`;

export const targetTypeToTitle = (targetType: TargetType): string => {
  const targetTypeInfo = targetTypes.find(i => i.targetType === targetType);
  return targetTypeInfo === undefined ? '' : targetTypeInfo.title;
};

export const targetTypeToSubTitle = (targetType: TargetType): string => {
  const targetTypeInfo = targetTypes.find(i => i.targetType === targetType);
  return targetTypeInfo === undefined ? '' : targetTypeInfo.subTitle;
};

export const getTargetAmountValue = (targetAmount: TargetAmount): number =>
  targetAmount;

export const calculatePercentage = (
  targetAmount: TargetAmount,
  currentAmount: Target['assetAmount']
): number => Math.floor((currentAmount / targetAmount) * 100);

export const howToSaveTypeToImg = (type: HowToSaveType): string => {
  switch (type) {
    case howToSaveInvestmentProducts:
      return '/static/images/advisor/investment2.svg';
    case howToSaveCash:
      return '/static/images/advisor/cash2.svg';
  }
};

export const howToSaveTypeToTitle = (type: HowToSaveType): string => {
  switch (type) {
    case howToSaveInvestmentProducts:
      return '投資商品で運用';
    case howToSaveCash:
      return '現金でつみたて';
  }
};
